import { inject, Type } from '@angular/core';
import { Route, Routes } from '@angular/router';
import { ComputersLegacyComponent } from '@components/computers-legacy/computers-legacy.component';
import { HelpComponent } from '@components/help/help.component';
import { MarketingIframeComponent } from '@components/marketing-iframe/marketing-iframe.component';
import { NotFoundComponent } from '@components/not-found/not-found.component';
import { SidepanelComputersWrapperComponent } from '@components/sidepanel-computers-wrapper/sidepanel-computers-wrapper.component';
import { brandTitle } from '@domains/login/utils/brand-title';
import { environment } from '@environments/environment';
import { EmptyPageComponent } from '@modules/empty-page/empty-page.component';
import { defaultFaviconGuard } from '@services/favicon.service';
import { AppLayoutComponent } from '@shared/_layout/app-layout/app-layout.component';
import { DataChangeWatcherGuard } from '@shared/guards/data-change-watcher.guard';
import { documentsGuard } from '@shared/guards/documents.guard';
import { globalAgentOptionsContentGuard } from '@shared/guards/global-agent-options-content.guard';
import { LicensesGuard } from '@shared/guards/licenses.guard';
import { MaintenanceActivateChild } from '@shared/guards/maintenance.guard';
import { notificationsContentGuard } from '@shared/guards/notifications-content.guard';
import { PermissionsGuard } from '@shared/guards/permissions.guard';
import { PremiumSupportGuard } from '@shared/guards/premiumSupport.guard';
import { supportPortalGuard } from '@shared/guards/supportPortal.guard';
import { productTitle } from '@utils/product-title';
import { I18NEXT_NAMESPACE_RESOLVER } from 'angular-i18next';
import { NotFoundWrapperComponent } from 'mbs-ui-shared@components/not-found-wrapper/not-found-wrapper.component';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { CurrentUserGuard } from 'mbs-ui-shared@guards/currentUser.guard';
import { PermissionsEnum } from 'mbs-ui-shared@models';

export const appsDomainsModule = 'backup-statistics';
export const settingsModule = 'settings';
export const rmmGroupTaskModule = 'rmm-group-task';
export const notificationModule = 'notifications';
export const computersModule = 'computers';
export const passRecoveryModule = 'pass-recovery';

function getDemo(path: string, loader: () => Promise<Type<any>>): Route {
  return {
    path,
    data: {
      i18nextNamespaces: [],
      isDemo: true
    },
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    },
    loadChildren: () => {
      if (environment.production) {
        return null;
      }

      return loader();
    }
  };
}

export const iframePagesWithStandardLayout: Routes = [
  { path: 'ADIntegration.aspx', component: EmptyPageComponent },
  { path: 'AddAdmin.aspx', component: EmptyPageComponent },
  { path: 'AddUser.aspx', component: EmptyPageComponent },
  {
    path: 'AdminProfile.aspx',
    component: EmptyPageComponent,
    data: {
      permissions: ['SubAdmin']
    }
  },
  { path: 'Administrators.aspx', component: EmptyPageComponent },
  { path: 'AuditLog.aspx', component: EmptyPageComponent },
  { path: 'AzureRestoreWizard.aspx', component: EmptyPageComponent },
  { path: 'BackupHistory.aspx', component: EmptyPageComponent },
  { path: 'Billing.aspx', component: EmptyPageComponent },
  { path: 'Calculator.aspx', component: EmptyPageComponent },
  { path: 'calc.aspx', component: EmptyPageComponent },
  { path: 'ClientSettings.aspx', component: EmptyPageComponent },
  { path: 'Companies.aspx', component: EmptyPageComponent },
  { path: 'CompareBackupEditions.aspx', component: EmptyPageComponent },
  {
    path: 'Computers.aspx',
    component: ComputersLegacyComponent,
    resolve: {
      i18next: I18NEXT_NAMESPACE_RESOLVER
    },
    children: [{ path: ':hid', component: SidepanelComputersWrapperComponent }] // todo remove after completed migration
  },
  { path: 'ConfigList.aspx', component: EmptyPageComponent },
  {
    path: 'Default.aspx',
    component: EmptyPageComponent,
    data: {
      permissions: [PermissionsEnum.Dashboard]
    }
  },
  { path: 'DownloadInvoice.aspx', component: EmptyPageComponent },
  { path: 'EC2.aspx', component: EmptyPageComponent },
  { path: 'EC2RestoreHistory.aspx', component: EmptyPageComponent },
  { path: 'EC2RestoreWizard.aspx', component: EmptyPageComponent },
  { path: 'EC2VolumeSnapshots.aspx', component: EmptyPageComponent },
  { path: 'EC2Volumes.aspx', component: EmptyPageComponent },
  { path: 'Ec2History.aspx', component: EmptyPageComponent },
  { path: 'EditAccount.aspx', component: EmptyPageComponent },
  { path: 'EditAdmin.aspx', component: EmptyPageComponent },
  { path: 'EditEC2Plan.aspx', component: EmptyPageComponent },
  { path: 'EditPlan2.aspx', component: EmptyPageComponent },
  { path: 'EditUser.aspx', component: EmptyPageComponent },
  { path: 'EmailsCustomization.aspx', component: EmptyPageComponent },
  { path: 'ForgotPass.aspx', component: EmptyPageComponent },

  // Added old `Apps` redirect links for support. Will be removed from release 6.5 and up.
  { path: 'Apps/General.aspx', redirectTo: RoutingPath.ApAppsGeneral },
  { path: 'Apps.aspx', redirectTo: RoutingPath.ApAppsGeneral },
  { path: 'GoogleApps.aspx', redirectTo: RoutingPath.ApAppsGeneral },

  { path: 'Apps/Domains.aspx', redirectTo: RoutingPath.ApAppsDomains },
  { path: 'BackupStatistics/Domains.aspx', redirectTo: RoutingPath.ApAppsDomains },
  { path: 'AppsDomains.aspx', redirectTo: RoutingPath.ApAppsDomains },
  { path: 'GoogleAppsDomains.aspx', redirectTo: RoutingPath.ApAppsDomains },

  // Reports.aspx page was completely deleted
  { path: 'Apps/Reports.aspx', redirectTo: RoutingPath.ApAppsDomains },
  { path: 'GappsUsersReport.aspx', redirectTo: RoutingPath.ApAppsDomains },
  { path: 'AppsUsersReport.aspx', redirectTo: RoutingPath.ApAppsDomains },
  // end

  { path: 'GoogleAppsBilling.aspx', component: EmptyPageComponent },
  { path: 'IntegrationAutotask.aspx', component: EmptyPageComponent },
  { path: 'IntegrationConnWise.aspx', component: EmptyPageComponent },
  { path: 'IntegrationConnectWiseList.aspx', component: EmptyPageComponent },
  { path: 'IntegrationLabTech.aspx', redirectTo: '/AP/Help/integrations/connectwise-automate' },
  { path: 'IntegrationOptiTune.aspx', redirectTo: '/AP/Help/integrations/optitune' },
  {
    path: 'LicenseActivation.aspx',
    component: EmptyPageComponent,
    data: {
      permissions: ['LicenseActivate']
    }
  },
  {
    path: 'LicenseUsageHistory.aspx',
    component: EmptyPageComponent,
    data: {
      permissions: [PermissionsEnum.LicenseUsageHistory]
    }
  },
  {
    path: 'Licenses.aspx',
    canActivate: [LicensesGuard],
    component: EmptyPageComponent,
    data: {
      permissions: [PermissionsEnum.Licenses]
    }
  },
  { path: 'LicensesLegacy.aspx', component: EmptyPageComponent },
  { path: 'LicensesSubscriptionHistory.aspx', component: EmptyPageComponent },
  { path: 'ManageEc2Plans.aspx', component: EmptyPageComponent },
  { path: 'Monitoring.aspx', component: EmptyPageComponent },
  { path: 'NewPassword.aspx', component: EmptyPageComponent },
  { path: 'Notifications.aspx', component: EmptyPageComponent },
  { path: 'OnlineAccess.aspx', component: EmptyPageComponent },
  { path: 'Plans.aspx', component: EmptyPageComponent },
  { path: 'PolicyDesigner.aspx', component: EmptyPageComponent },
  { path: 'RMBackupHistory.aspx', component: EmptyPageComponent },
  { path: 'RebrandResources.aspx', component: EmptyPageComponent },
  { path: 'Rebranding.aspx', component: EmptyPageComponent },
  { path: 'RemoteDeployConfiguration.aspx', component: EmptyPageComponent },
  { path: 'ReportDownload.aspx', component: EmptyPageComponent },
  { path: 'ScheduledReports.aspx', component: EmptyPageComponent },
  {
    path: 'Settings.aspx',
    component: EmptyPageComponent,
    data: {
      permissions: [PermissionsEnum.Provider]
    }
  },
  { path: 'SiteMap.aspx', component: EmptyPageComponent },
  { path: 'StandaloneMarketplaceLicenses.aspx', component: EmptyPageComponent },
  { path: 'StorageCapacityReport.aspx', component: EmptyPageComponent },
  { path: 'StorageCostSettings.aspx', component: EmptyPageComponent },
  { path: 'StorageUsageReport.aspx', component: EmptyPageComponent },
  { path: 'StorageUtilization.aspx', component: EmptyPageComponent },
  { path: 'SubmitTicketForm.aspx', component: EmptyPageComponent },
  { path: 'Tariffs.aspx', component: EmptyPageComponent },
  { path: 'UserList.aspx', component: EmptyPageComponent },
  { path: 'UserPlanReport.aspx', component: EmptyPageComponent },
  { path: 'BackupHistoryLegacy', component: EmptyPageComponent },
  { path: 'BackupHistoryLegacy/', component: EmptyPageComponent },
  { path: 'BackupHistoryLegacy/:id', component: EmptyPageComponent },
  { path: 'ConnectWise/Authentication', component: EmptyPageComponent },
  { path: 'ConnectWise/Billing', component: EmptyPageComponent },
  { path: 'ConnectWise/Contacts', component: EmptyPageComponent },
  { path: 'ConnectWise/Tickets', component: EmptyPageComponent }
];

export const routes: Routes = [
  {
    path: 'AP',
    canActivate: [CurrentUserGuard],
    canActivateChild: [
      CurrentUserGuard // Check finish account init steps
    ],
    children: [
      {
        path: 'StayLoggedIn',
        title: brandTitle,
        loadComponent: () => import('@domains/login/pages/stay-logged-in/stay-logged-in.component').then((m) => m.StayLoggedInComponent),
        data: {
          i18nextNamespaces: ['login']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        }
      },
      {
        path: 'LoginMcp',
        title: productTitle,
        loadComponent: () => import('@domains/login/pages/login-mcp/login-mcp.component').then((m) => m.LoginMcpComponent),
        data: {
          i18nextNamespaces: ['login']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        }
      },
      {
        path: 'GettingStart',
        title: productTitle,
        data: {
          i18nextNamespaces: ['wizards']
        },
        loadChildren: () => import('@domains/onboarding/onboarding.module').then((m) => m.OnboardingModule),
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        }
      },
      {
        path: 'Azure/OAuth',
        loadChildren: () => import('@domains/azure-oauth/azure-oauth.module').then((m) => m.AzureOauthModule)
      },
      {
        path: 'Google/OAuth',
        loadChildren: () => import('@domains/google-oauth/google-oauth.module').then((m) => m.GoogleOauthModule)
      },
      {
        path: 'AuthorizationComputer',
        title: brandTitle,
        loadComponent: () =>
          import('@domains/authorization-computer/authorization-computer.component').then((m) => m.AuthorizationComputerComponent)
      }
    ]
  },
  {
    path: 'AP',
    children: [
      {
        path: 'SupportPortal',
        component: EmptyPageComponent,
        canActivateChild: [supportPortalGuard],
        canActivate: [supportPortalGuard],
        children: [
          {
            path: '**',
            component: EmptyPageComponent
          }
        ]
      }
    ]
  },
  {
    path: 'AP',
    children: [
      {
        path: 'PremiumSupport',
        component: EmptyPageComponent,
        canActivate: [CurrentUserGuard, PremiumSupportGuard]
      }
    ]
  },
  {
    // Outside the layout on purpose: the viewer takes the whole page, with no header or footer over it.
    // The guards are split the way the layout splits them: the permission is asked as a child guard, so
    // that it is asked after the user has been loaded, and with them what they are allowed to read.
    path: 'AP',
    canActivate: [CurrentUserGuard],
    canActivateChild: [PermissionsGuard],
    children: [
      {
        path: 'Documents/:id',
        canActivate: [documentsGuard],
        loadComponent: () => import('@domains/documents/pages/document-view/document-view.component').then((c) => c.DocumentViewComponent)
      }
    ]
  },
  {
    path: 'AP',
    component: AppLayoutComponent,
    canActivate: [CurrentUserGuard],
    canActivateChild: [
      PermissionsGuard,
      CurrentUserGuard, // Check finish account init steps,
      MaintenanceActivateChild
    ],
    children: [
      {
        path: 'Assets',
        data: {
          permissions: [PermissionsEnum.RemoteManagement],
          i18nextNamespaces: ['assets']
        },
        resolve: { i18next: I18NEXT_NAMESPACE_RESOLVER },
        loadChildren: () => import('@domains/assets/assets.routes').then((r) => r.ASSETS_ROUTES)
      },
      {
        path: 'Documents',
        canActivate: [documentsGuard],
        loadChildren: () => import('@domains/documents/documents.routes').then((r) => r.DOCUMENTS_ROUTES)
      },
      {
        path: 'Dashboard',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: { permissions: [PermissionsEnum.Dashboard], i18nextNamespaces: ['dashboard'] },
        resolve: { i18next: I18NEXT_NAMESPACE_RESOLVER },
        loadChildren: () => import('@domains/dashboard/dashboard.module').then((m) => m.DashboardModule)
      },
      {
        path: 'Monitoring',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: { permissions: [PermissionsEnum.Monitoring, PermissionsEnum.Backup], i18nextNamespaces: ['monitoring'] },
        resolve: { i18next: I18NEXT_NAMESPACE_RESOLVER },
        loadChildren: () => import('@domains/monitoring/monitoring.module').then((m) => m.MonitoringModule)
      },
      {
        path: 'WebConsoleRebranding',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: { permissions: ['WebConsoleRebranding'], i18nextNamespaces: ['web-console-rebranding'] },
        resolve: { i18next: I18NEXT_NAMESPACE_RESOLVER },
        loadChildren: () =>
          import('@domains/web-console-rebranding/web-console-rebranding.module').then((m) => m.WebConsoleRebrandingModule)
      },
      {
        path: 'StorageLimits',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: { permissions: [PermissionsEnum.Package], i18nextNamespaces: ['storage-limits'] },
        resolve: { i18next: I18NEXT_NAMESPACE_RESOLVER },
        loadChildren: () => import('@domains/storage-limits/storage-limits.module').then((m) => m.StorageLimitsModule)
      },
      {
        path: 'EmailsCustomization',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: { i18nextNamespaces: ['emails-customization'] },
        resolve: { i18next: I18NEXT_NAMESPACE_RESOLVER },
        loadChildren: () => import('@domains/emails-customization/emails-customization.module').then((m) => m.EmailsCustomizationModule)
      },
      {
        path: 'StorageUsage',
        data: { permissions: ['StorageUsage'], i18nextNamespaces: ['storage-usage'] },
        resolve: { i18next: I18NEXT_NAMESPACE_RESOLVER },
        loadChildren: () => import('@domains/storage-usage/storage-usage.module').then((m) => m.StorageUsageModule)
      },
      {
        path: 'Tickets',
        data: {
          permissions: ['AllowTicketingSubsystem', PermissionsEnum.ShowTickets],
          i18nextNamespaces: ['tickets']
        },
        resolve: { i18next: I18NEXT_NAMESPACE_RESOLVER },
        loadChildren: () => import('mbs-ui-shared@components/tickets/tickets.routes').then((r) => r.TICKETS_ROUTES)
      },
      { path: 'BackupHistory', redirectTo: 'newBackupHistory' },
      getDemo('RecoveryStatusDemo', async () => {
        const m = await import('mbs-ui-shared@modules/recovery-status/demo/recovery-status-demo.module');

        return m.RecoveryStatusDemoModule;
      }),
      getDemo('TicketsDemo', async () => {
        const m = await import('mbs-ui-shared@components/tickets/demo/tickets-demo.module');

        return m.TicketsDemoModule;
      }),
      {
        path: 'ScriptLibrary',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        loadChildren: () => import('@domains/script-library/script-library.module').then((m) => m.ScriptLibraryModule),
        data: {
          i18nextNamespaces: ['rmm-script-library'],
          permissions: [PermissionsEnum.Rmm]
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        }
      },
      {
        path: 'Snmp',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          i18nextNamespaces: ['snmp'],
          permissions: [PermissionsEnum.Rmm]
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/snmp/snmp.module').then((m) => m.SnmpModule)
      },
      {
        path: 'RMMNotifications',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          permissions: [PermissionsEnum.Rmm]
        },
        loadChildren: () => import('@domains/rmm-notifications/rmm-notifications.module').then((m) => m.RmmNotificationsModule)
      },
      {
        path: 'RMMApiTokens',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          i18nextNamespaces: ['settings'],
          permissions: [PermissionsEnum.Notification, PermissionsEnum.Provider, PermissionsEnum.Rmm, PermissionsEnum.ManageRmmApiTokens]
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/rmm-public-api/rmm-public-api.module').then((m) => m.RmmPublicAPIPageModule)
      },
      { path: 'rmm-page', redirectTo: 'RMMPage', pathMatch: 'full' },
      {
        path: 'RMMPage',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          i18nextNamespaces: [computersModule, 'rmm-page'],
          permissions: []
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/rmm-page/rmm-page.module').then((m) => m.RmmPageModule)
      },
      {
        path: 'Computers',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          permissions: [PermissionsEnum.RemoteManagement],
          i18nextNamespaces: [computersModule]
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/computers/computers.module').then((m) => m.ComputersModule)
      },
      {
        path: 'Backup/Computers',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          permissions: [PermissionsEnum.RemoteManagement, PermissionsEnum.Backup],
          i18nextNamespaces: [computersModule]
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/computers/computers.module').then((m) => m.ComputersModule)
      },
      {
        path: 'RMM/Computers',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          permissions: [PermissionsEnum.RemoteManagement, PermissionsEnum.Rmm],
          i18nextNamespaces: [computersModule]
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/computers/computers.module').then((m) => m.ComputersModule)
      },
      {
        path: 'Administrators',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          permissions: [PermissionsEnum.Administrator],
          i18nextNamespaces: ['administrators']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/administrators/administrators.module').then((m) => m.AdministratorsModule)
      },
      {
        path: 'Settings',
        loadChildren: () => import('@domains/settings/settings.module').then((m) => m.SettingsModule),
        data: {
          i18nextNamespaces: [settingsModule]
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        }
      },
      {
        path: 'Companies',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        loadChildren: () => import('@domains/companies/companies.module').then((m) => m.CompaniesModule),
        data: {
          permissions: [PermissionsEnum.ManageCompanies],
          i18nextNamespaces: ['companies']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        }
      },
      {
        path: 'RestoreToCloud',
        loadChildren: () => import('@domains/cloud-restore/cloud-restore.module').then((m) => m.CloudRestoreModule),
        data: {
          i18nextNamespaces: ['cloud-restore'],
          permissions: [PermissionsEnum.CloudRestore, PermissionsEnum.Backup]
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        }
      },
      {
        path: 'Apps',
        pathMatch: 'full',
        redirectTo: RoutingPath.ApNotFound
      },
      {
        path: 'Apps',
        children: [
          {
            path: 'Domains',
            canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
            loadChildren: () => import('@domains/m365-google/domains.module').then((m) => m.DomainsModule),
            data: {
              i18nextNamespaces: [appsDomainsModule, 'licenses']
            },
            resolve: {
              i18next: I18NEXT_NAMESPACE_RESOLVER
            }
          },
          {
            path: 'DomainUsers',
            canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
            loadChildren: () => import('@domains/m365-google/domain-users.module').then((m) => m.DomainUsersModule),
            data: {
              i18nextNamespaces: [appsDomainsModule, 'licenses']
            },
            resolve: {
              i18next: I18NEXT_NAMESPACE_RESOLVER
            }
          }
        ]
      },
      {
        path: 'GlobalAgentOptions',
        canActivate: [globalAgentOptionsContentGuard],
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        loadChildren: () => import('@domains/global-agent-options/global-agent-options.module').then((m) => m.GlobalAgentOptionsModule),
        data: {
          permissions: [PermissionsEnum.AccessToAllCompanies]
        }
      },
      {
        path: 'Statistics',
        loadChildren: () => import('@domains/statistics/statistics.module').then((m) => m.StatisticsModule)
      },
      {
        path: 'RDStatistics',
        loadChildren: () => import('@domains/statistics/statistics.module').then((m) => m.StatisticsModule),
        data: {
          permissions: [PermissionsEnum.RemoteManagement, PermissionsEnum.ShowRemoteConnection]
        }
      },
      {
        path: 'Branding',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          permissions: [PermissionsEnum.Rebranding],
          i18nextNamespaces: ['branding']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/brands/brands.module').then((m) => m.BrandsModule)
      },
      {
        path: 'Notifications',
        canActivate: [notificationsContentGuard],
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        loadChildren: () => import('./domains/notifications/notifications.module').then((m) => m.NotificationsModule),
        data: {
          permissions: [PermissionsEnum.Notification],
          i18nextNamespaces: [notificationModule]
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        }
      },
      {
        path: 'PlanReportSettings',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          permissions: [PermissionsEnum.Notification],
          i18nextNamespaces: ['plan-report-settings']
        },
        loadChildren: () => import('@domains/plan-report-settings/plan-report-settings.module').then((m) => m.PlanReportSettingsModule),
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        }
      },
      {
        path: 'RemoteDeploy',
        redirectTo: 'BackupTemplates'
      },
      {
        path: 'BackupTemplates',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          permissions: [PermissionsEnum.RemoteDeploy, PermissionsEnum.Backup],
          i18nextNamespaces: ['templates']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/remote-deploy/remote-deploy.module').then((m) => m.RemoteDeployModule)
      },
      {
        path: 'Users',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          i18nextNamespaces: ['users']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/users/users.module').then((m) => m.UsersModule)
      },
      {
        path: 'AuditLog',
        data: {
          i18nextNamespaces: ['audit-log']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/audit-log/audit-log.module').then((m) => m.AuditLogModule)
      },
      {
        path: 'Help',
        children: [
          {
            path: '**',
            component: HelpComponent
          }
        ],
        data: {
          permissions: [PermissionsEnum.HelpMarketing]
        }
      },
      {
        path: 'EmailService',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          permissions: [PermissionsEnum.Notification],
          i18nextNamespaces: ['email-service-settings']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () =>
          import('@domains/email-service-settings/email-service-settings.module').then((m) => m.EmailServiceSettingsModule)
      },
      ...iframePagesWithStandardLayout,
      {
        path: 'SSL',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          permissions: ['CustomCertificatesBinding'],
          i18nextNamespaces: ['binding-certificates']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/bindings-certificates/bindings-certificates.module').then((m) => m.BindingsCertificatesModule)
      },
      {
        path: 'Marketing',
        component: MarketingIframeComponent,
        data: {
          permissions: [PermissionsEnum.HelpMarketing]
        }
      },
      {
        path: 'Licenses',
        canLoad: [CurrentUserGuard],
        canActivate: [LicensesGuard],
        data: {
          permissions: ['Licenses'],
          i18nextNamespaces: ['licenses']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/licenses/licenses.module').then((m) => m.LicensesModule)
      },
      {
        path: 'NotificationErrors',
        data: {
          i18nextNamespaces: ['notification-errors'],
          permissions: [PermissionsEnum.Notification]
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/notification-errors/notification-errors.module').then((m) => m.NotificationErrorsModule)
      },
      {
        path: 'StorageCapacityReport',
        data: {
          permissions: [PermissionsEnum.CapacityReport],
          i18nextNamespaces: ['capacity-report']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/capacity-report/capacity-report.module').then((m) => m.CapacityReportModule)
      },
      {
        path: 'PurchaseHistory',
        data: {
          permissions: [PermissionsEnum.PurchaseHistory],
          i18nextNamespaces: ['purchase-history']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () =>
          import('mbs-ui-shared@components/purchase-history/purchase-history.routes').then((r) => r.PURCHASE_HISTORY_ROUTES)
      },
      {
        path: 'RMMAlertsHistory',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          i18nextNamespaces: ['rmm-alerts-history'],
          permissions: [PermissionsEnum.Notification, PermissionsEnum.Rmm]
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/alerts-history/alerts-history.module').then((m) => m.AlertsHistoryModule)
      },
      {
        path: 'ConnectWiseNew',
        data: {
          i18nextNamespaces: ['connect-wise'],
          permissions: ['PSA']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/connect-wise/connect-wise.module').then((m) => m.ConnectWiseModule)
      },
      {
        path: 'HaloPSA',
        data: {
          i18nextNamespaces: ['halo-psa'],
          permissions: ['PSA']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/halo-psa/halo-psa.module').then((m) => m.HaloPSAModule)
      },
      {
        path: 'TicketSettings',
        data: {
          i18nextNamespaces: ['tickets'],
          permissions: ['PSA']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () =>
          import('@domains/mbs-ticket-integration/mbs-ticket-integration.module').then((m) => m.MBSTicketIntegrationModule)
      },
      {
        path: 'Billing',
        data: {
          i18nextNamespaces: ['billing'],
          permissions: ['PSA']
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/billing/billing.module').then((m) => m.BillingModule)
      },
      {
        path: 'RMMGroupTaskAction',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          i18nextNamespaces: [rmmGroupTaskModule],
          permissions: [PermissionsEnum.Rmm]
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('@domains/group-action-task/group-action-task.module').then((m) => m.GroupActionTaskModule)
      },
      {
        path: 'ScheduledReports',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: { permissions: [PermissionsEnum.Notification], i18nextNamespaces: ['scheduled-reports'] },
        resolve: { i18next: I18NEXT_NAMESPACE_RESOLVER },
        loadChildren: () => import('@domains/scheduled-reports/scheduled-reports.module').then((m) => m.ScheduledReportsModule)
      },
      {
        path: 'Invoices',
        loadChildren: () => import('@domains/invoices/invoices.module').then((m) => m.InvoicesModule)
      },
      {
        path: 'newBackupHistory',
        canDeactivate: [() => inject(DataChangeWatcherGuard).canDeactivate()],
        data: {
          i18nextNamespaces: ['backup-history'],
          permissions: [PermissionsEnum.BackupHistory, PermissionsEnum.Backup]
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        },
        loadChildren: () => import('./domains/backup-history/backup-history.module').then((m) => m.BackupHistoryModule)
      },
      {
        path: 'StorageAccounts',
        loadChildren: () => import('@domains/storages/storages.module').then((m) => m.StoragesModule),
        data: {
          i18nextNamespaces: ['storages'],
          permissions: [PermissionsEnum.Storage]
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        }
      },
      {
        path: 'BackupStorage',
        loadChildren: () => import('@domains/storage-tree/storage-tree.module').then((m) => m.StorageTreeModule),
        data: {
          i18nextNamespaces: ['storages'],
          permissions: [PermissionsEnum.Storage]
        },
        resolve: {
          i18next: I18NEXT_NAMESPACE_RESOLVER
        }
      },
      {
        path: 'NotFound',
        component: NotFoundComponent
      },
      {
        path: 'Maintenance',
        loadComponent: () =>
          import('mbs-ui-shared@components/maintenance-page/maintenance-page.component').then((m) => m.MaintenancePageComponent)
      },
      { path: '', redirectTo: RoutingPath.ApNotFound, pathMatch: 'full' }
    ]
  },
  {
    path: 'AS',
    children: [
      {
        path: 'Payment',
        title: productTitle,
        canActivate: [defaultFaviconGuard],
        data: { i18nextNamespaces: ['payment'] },
        resolve: { i18next: I18NEXT_NAMESPACE_RESOLVER },
        loadComponent: () => import('@domains/payment/payment.component').then((m) => m.PaymentComponent)
      }
    ]
  },
  {
    path: 'AS',
    loadChildren: () => import('./shared/_layout/unauthorized-layout/unauthorized-layout.module').then((m) => m.UnauthorizedLayoutModule)
  },
  // no layout routes
  { path: 'login', redirectTo: RoutingPath.Login },
  { path: 'Admin/payment.aspx', component: EmptyPageComponent, title: productTitle, canActivate: [defaultFaviconGuard] },

  // otherwise redirect to home
  { path: '**', component: NotFoundWrapperComponent }
];
